import { ArcElement,Chart as ChartJS,Legend,Tooltip } from "chart.js";
import { TreemapController,TreemapElement } from "chartjs-chart-treemap";
import { useEffect,useState } from "react";
import { Chart } from "react-chartjs-2";

// Register required Chart.js components including TreemapController
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend,
  TreemapController,
  TreemapElement
);

// Modern color palette with brand colors
const colors = [
  "#D3001A", // Red (Brand)
  "#f59e0b", // Orange (Brand)
  "#fbbf24", // Yellow (Brand)
  "#10b981", // Green
  "#3b82f6", // Blue
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#06b6d4", // Cyan
  "#f97316", // Orange-2
  "#84cc16", // Lime
  "#6366f1", // Indigo
  "#14b8a6"  // Teal
];

const EmployeesByDepartmentChart = ({ departments }) => {
  const [chartData, setChartData] = useState(null);
  
  useEffect(() => {
    if (departments.length === 0) return;
    
    const MIN_SIZE = 70;
    
    // Prepare data for Chart.js
    const data = departments.map(d => ({
      department: d.name, 
      value: Math.max(d.count, MIN_SIZE),
      actualCount: d.count,
      id: d.id_
    }));
    
    setChartData({
      datasets: [{
        tree: data,
        key: "value",
        labels: {
          display: true,
          align: 'center',
          position: 'middle',
          formatter: (ctx) => {
            // First try to truncate department name if it's too long
            const dept = ctx.raw._data.department;
            const count = ctx.raw._data.actualCount;
            const truncatedDept = dept.length > 15 ? dept.substring(0, 12) + '...' : dept;
            return [truncatedDept, `${count} employees`];
          },
          font: {
            size: 12,
            weight: '600',
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          },
          color: '#FFFFFF',
          overflow: 'fit',
          padding: 8
        },
        backgroundColor: (ctx) => {
          const baseColor = colors[ctx.dataIndex % colors.length];
          // Add subtle gradient effect
          return baseColor;
        },
        hoverBackgroundColor: (ctx) => {
          const baseColor = colors[ctx.dataIndex % colors.length];
          // Slightly lighter on hover
          return baseColor;
        },
        spacing: 3,
        borderWidth: 2,
        borderColor: "#ffffff",
        borderSkipped: false,
        borderRadius: 8
      }]
    });
  }, [departments]);

  // Handle chart element click to open employees page with department filter
  const handleClick = (event, elements) => {
    if (elements.length > 0) {
      const clickedElement = elements[0];
      const id = clickedElement.element.$context.raw._data.id;
      // Open in new tab with department parameter
      window.open(`/employees?department=${id}`, '_blank');
    }
  };

  if (departments.length === 0 || !chartData) return null;

  const options = {
    plugins: {
      datalabels: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: 'rgba(0, 0, 0, 0.08)',
        borderWidth: 1,
        padding: 16,
        cornerRadius: 8,
        displayColors: true,
        boxWidth: 12,
        boxHeight: 12,
        titleFont: {
          size: 14,
          weight: '600',
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        },
        bodyFont: {
          size: 13,
          weight: '500',
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        },
        callbacks: {
          title: () => '',
          label: (context) => {
            const dept = context.raw._data.department;
            const count = context.raw._data.actualCount;
            return [`${dept}`, `${count} employees`];
          }
        }
      },
      legend: {
        display: false
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    caretSize: 0, // Remove caret from tooltip
    caretPadding: 10,
    onClick: handleClick, // Add click handler to chart options
    interaction: {
      intersect: false,
      mode: 'point'
    },
    animation: {
      duration: 800,
      easing: 'easeInOutQuart'
    },
    onHover: (event, activeElements) => {
      if (activeElements.length > 0) {
        event.native.target.style.cursor = 'pointer';
      } else {
        event.native.target.style.cursor = 'default';
      }
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: "0px",
        width: "100%",
        margin: "auto",
        height: "100%",
        minHeight: "180px"
      }}
    >
      {chartData && (
        <Chart
          type="treemap"
          data={chartData}
          options={options}
          height={180}
          width={"100%"}
        />
      )}
    </div>
  );
};

export default EmployeesByDepartmentChart;
